import React from "react";
import { gql } from "graphql-tag";
import { RouteComponentProps } from "@reach/router";
import { Card, CardHeader } from "@material-ui/core";
import * as TOML from "@ltd/j-toml";
import { useMutation, useQuery } from "@apollo/client";
import { updateFormDefinition, updateFormDefinitionVariables, updateFormDefinitionForm } from "types/gql-types";
import { LoadingSpinner } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import DefinitionsForm, { FormDefinition, defaultFormSchema } from "./form";

const GET_DEFINITION = gql`
  query updateFormDefinitionForm($id: ID!) {
    formDefinition(id: $id) {
      id
      name
      content
      enabled
      createInterest
      propertyId
      formType
      skipSignature
      renderParagraphs
      ageRequirement
      ageRequirementOrDisabilityOverride
      formId
      form {
        id
        open
        display
      }
    }
  }
`;

const UPDATE_DEFINITION = gql`
  mutation updateFormDefinition($id: ID!, $data: FormDefinitionInput!) {
    updateFormDefinition(id: $id, data: $data) {
      id
      name
      content
      enabled
      createInterest
      propertyId
      formType
      skipSignature
      renderParagraphs
      ageRequirement
      ageRequirementOrDisabilityOverride
      formId
    }
  }
`;

const verifyUpdate = (data: FormDefinition) => {
  if (!data.enabled) return true;

  try {
    TOML.parse[0.5](data.content || "", "\n");
    return true;
  } catch (e) {
    return false;
  }
};

const UpdateFormDefinition: React.FC<RouteComponentProps<{id?: string}>> = ({ id }) => {
  const { openSnackbar } = useSnackbar();
  const { loading, error, data } = useQuery<updateFormDefinitionForm>(GET_DEFINITION, {
    fetchPolicy: "cache-and-network",
    variables: { id },
  });
  const [updateForm] = useMutation<
  updateFormDefinition,
  updateFormDefinitionVariables
  >(UPDATE_DEFINITION);

  if (!id) return null;

  const submitForm = async (formData: FormDefinition) => {
    if (!verifyUpdate(formData)) {
      return openSnackbar({ message: "Update failed, TOML invalid", status: "error" });
    }

    try {
      const { form: formInfo, ...updatedData } = defaultFormSchema.cast({ ...formData }) as FormDefinition;
      const dataToLoad = { formOpen: formInfo && formInfo.open, ...updatedData };
      await updateForm({ variables: { id, data: dataToLoad } });
      openSnackbar({ message: "Update Successful", status: "success" });
    } catch (err) {
      return logger.sendError(err as Error);
    }
  };

  if (error) <span>Server Error</span>;
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const formDefinition = defaultFormSchema.cast({ ...data.formDefinition }) as FormDefinition;

  formDefinition.id = id;

  return (
    <Card>
      <CardHeader title="Update a Form Draft" subheader="Required fields marked with *" />
      <DefinitionsForm
        formType="update"
        initialData={formDefinition}
        handleSubmit={submitForm}
      />
    </Card>
  );
};

export default UpdateFormDefinition;
